import Text from 'components/_UI/Text'
import { useTags } from 'context/events/Tags'
import React, { useState, useEffect } from 'react'
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors'
import tagsApi from 'api/requests/tags'
import Loader from 'components/_UI/Loader'
import paths from 'routes/paths'
import styled, { keyframes, css } from 'styled-components'
import { useUser } from 'context/user/User'
import userApi from 'api/requests/user'
import step from 'helpers/system/step'
import { FullyCentered } from 'styled/alignment/Center'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useStepRegister } from 'context/login/StepRegister'
import { getCategoryIcon } from 'modules/events/categories/categoryIconMap'
import { Sparkles, ArrowRight, ChevronLeft, AlertCircle } from 'lucide-react'

const errorMessages = {
    tags: 'Ocorreu um erro ao carregar, tente novamente.',
    account: 'Ocorreu um erro ao criar sua conta, tente novamente.'
}

export default function OnboardingTags({ onComplete, hideBack }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { stepRegister, setStepRegister } = useStepRegister();
    const { tags, setTags } = useTags();
    const { user, setUser } = useUser();

    const [tagsError, setTagsError] = useState(false);
    const [createAccountError, setCreateAccountError] = useState(false);
    const [loadCategories, setLoadCategories] = useState(false);
    const [loadSubmit, setLoadSubmit] = useState(false);
    const [selectedTags, setSelectedTags] = useState();

    useEffect(() => {
        getCategories()
    }, [])

    async function getCategories() {
        if (tags) return;
        setLoadCategories(true);
        setTagsError(false);
        try {
            const { data } = await tagsApi.get();
            setTags(data);
        } catch (error) {
            setTagsError(true);
        } finally {
            setLoadCategories(false);
        }
    }

    function toggleTag(tag) {
        const isSelected = selectedTags?.tags?.some(t => t.id === tag.id);
        if (isSelected) {
            const newTags = (selectedTags?.tags || []).filter(t => t.id !== tag.id);
            setSelectedTags(newTags.length ? { tags: newTags } : null);
        } else {
            const newTag = { active: true, name: tag.name, id: tag.id };
            setSelectedTags({ tags: [...(selectedTags?.tags || []), newTag] });
        }
    }

    async function createAccount() {
        const tagIds = selectedTags?.tags?.map(tag => tag.id);

        if (onComplete) {
            onComplete(tagIds);
            return;
        }

        const payload = { ...user, tags: tagIds };

        setLoadSubmit(true);
        setCreateAccountError(false);
        setUser({ ...user, tags: selectedTags?.tags?.map(tag => tag.id) });

        try {
            await userApi.create(payload);
            navigate({
                pathname: paths.login,
                search: new URLSearchParams({
                    ...Object.fromEntries(searchParams),
                    confirmEmail: 1
                }).toString()
            });
        } catch (error) {
            setCreateAccountError(true);
        } finally {
            setLoadSubmit(false);
        }
    }

    const selectedCount = selectedTags?.tags?.length || 0;
    const hasSelection = selectedCount > 0;

    return (
        <Wrapper isModal={hideBack}>
            {/* ── Header ── */}
            {hideBack ? (
                <ModalHeader>
                    <HeaderIconRing>
                        <Sparkles size={18} color={colors.occasionalPurple} />
                    </HeaderIconRing>
                    <ModalTitle>Personalize sua experiência</ModalTitle>
                    <ModalSubtitle>Escolha os temas que mais te interessam</ModalSubtitle>
                </ModalHeader>
            ) : (
                <FullPageHeader>
                    <Text color={colors.nightDark} type='bold' name='highlight'>
                        Para finalizar, selecione as categorias
                    </Text>
                    <Text color={colors.nightDark} type='bold' name='highlight'>
                        que se encaixem no seu gosto.
                    </Text>
                </FullPageHeader>
            )}

            {/* ── Chips grid ── */}
            <ChipsArea isModal={hideBack}>
                {loadCategories ? (
                    <FullyCentered height='80px'>
                        <Loader />
                    </FullyCentered>
                ) : tagsError ? (
                    <ErrorBanner>
                        <AlertCircle size={15} color={colors.danger} />
                        <ErrorMsg>{errorMessages.tags}</ErrorMsg>
                    </ErrorBanner>
                ) : (
                    <ChipsGrid>
                        {tags?.map((tag) => {
                            const isSelected = selectedTags?.tags?.some(t => t.id === tag.id);
                            const Icon = getCategoryIcon(tag.icon);
                            return (
                                <TagChip
                                    key={tag.id}
                                    selected={isSelected}
                                    onClick={() => toggleTag(tag)}
                                >
                                    <Icon size={13} />
                                    <ChipLabel>{tag.name}</ChipLabel>
                                </TagChip>
                            );
                        })}
                    </ChipsGrid>
                )}
            </ChipsArea>

            {/* ── Actions ── */}
            <ActionsArea isModal={hideBack}>
                {loadSubmit ? (
                    <FullyCentered height='52px'>
                        <Loader />
                    </FullyCentered>
                ) : (
                    <>
                        <ConfirmButton
                            hasSelection={hasSelection}
                            onClick={hasSelection ? createAccount : undefined}
                            disabled={!hasSelection}
                        >
                            <span>
                                {hasSelection
                                    ? `Continuar com ${selectedCount} ${selectedCount === 1 ? 'selecionada' : 'selecionadas'}`
                                    : 'Selecione ao menos uma'
                                }
                            </span>
                            {hasSelection && <ArrowRight size={16} />}
                        </ConfirmButton>

                        {!hideBack && (
                            <BackButton onClick={() => setStepRegister(step.back(stepRegister))}>
                                <ChevronLeft size={15} />
                                <span>Voltar</span>
                            </BackButton>
                        )}
                    </>
                )}

                {createAccountError && (
                    <ErrorBanner style={{ marginTop: 12 }}>
                        <AlertCircle size={15} color={colors.danger} />
                        <ErrorMsg>{errorMessages.account}</ErrorMsg>
                    </ErrorBanner>
                )}
            </ActionsArea>
        </Wrapper>
    );
}

// ── Animations ──────────────────────────────────────────────────────────────

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const chipPop = keyframes`
    0%   { transform: scale(1); }
    40%  { transform: scale(0.93); }
    100% { transform: scale(1); }
`;

// ── Styled components ────────────────────────────────────────────────────────

const Wrapper = styled(Column)`
    width: 100%;
    animation: ${fadeUp} 0.3s ease-out both;

    ${({ isModal }) => isModal && css`
        padding: 24px 28px 28px;
    `}
`;

const ModalHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 20px;
`;

const HeaderIconRing = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${colors.occasionalPurple}12;
    border: 1.5px solid ${colors.occasionalPurple}28;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
`;

const ModalTitle = styled.h3`
    font-size: 17px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 4px 0;
    line-height: 1.3;
`;

const ModalSubtitle = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: 0;
    line-height: 1.4;
`;

const FullPageHeader = styled(Column)`
    margin-bottom: 8px;
`;

const ChipsArea = styled.div`
    width: 100%;
    overflow-y: auto;
    margin: 16px 0;

    ${({ isModal }) => isModal
        ? css`max-height: 280px;`
        : css`max-height: 340px;`
    }

    /* subtle scrollbar */
    scrollbar-width: thin;
    scrollbar-color: ${colors.occasionalPurple}40 transparent;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
        background: ${colors.occasionalPurple}40;
        border-radius: 4px;
    }
`;

const ChipsGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 2px 0 4px;
`;

const TagChip = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 13px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    line-height: 1;
    transition: background 0.14s ease, border-color 0.14s ease, color 0.14s ease, box-shadow 0.14s ease;
    user-select: none;
    outline: none;

    ${({ selected }) => selected ? css`
        background: ${colors.occasionalPurple}14;
        border: 1.5px solid ${colors.occasionalPurple};
        color: ${colors.occasionalPurple};
        box-shadow: 0 0 0 3px ${colors.occasionalPurple}0a;
        animation: ${chipPop} 0.2s ease-out;
    ` : css`
        background: transparent;
        border: 1.5px solid ${colors.nightLight}60;
        color: ${colors.nightDark};
    `}

    &:hover {
        border-color: ${colors.occasionalPurple}70;
        color: ${colors.occasionalPurple};
        background: ${colors.occasionalPurple}08;
    }

    &:active {
        transform: scale(0.95);
    }
`;

const ChipLabel = styled.span`
    white-space: nowrap;
    text-transform: capitalize;
`;

const ActionsArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;

    ${({ isModal }) => isModal && css`
        border-top: 1px solid ${colors.neutralDark};
        padding-top: 16px;
        margin-top: 4px;
    `}
`;

const ConfirmButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 13px 24px;
    border-radius: 50px;
    font-size: 14px;
    font-weight: 600;
    cursor: ${({ hasSelection }) => hasSelection ? 'pointer' : 'not-allowed'};
    border: none;
    transition: opacity 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;

    ${({ hasSelection }) => hasSelection ? css`
        background: ${colors.occasionalPurple};
        color: ${colors.neutralLight};
        box-shadow: 0 4px 14px ${colors.occasionalPurple}38;
        &:hover { opacity: 0.9; box-shadow: 0 6px 18px ${colors.occasionalPurple}48; }
        &:active { transform: scale(0.98); }
    ` : css`
        background: ${colors.neutralDark};
        color: ${colors.nightLight};
    `}
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.night};
    padding: 6px;
    border-radius: 8px;
    transition: color 0.15s ease;
    align-self: center;

    &:hover { color: ${colors.nightDark}; }
`;

const ErrorBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: #FEF2F2;
    border: 1px solid ${colors.dangerLight};
    border-radius: 8px;
    width: 100%;
`;

const ErrorMsg = styled.span`
    font-size: 13px;
    color: ${colors.danger};
`;
